import {
    isTrustedDocumentClassSource,
    resolveDocumentClassification,
} from './document-classification.js';
import { INCORPORATED_AUTHORITY_LIMITS } from './incorporated-authority-snapshot.js';

type LooseRecord = Record<string, unknown>;

export type JobDescriptionProvenanceSource =
    | 'ai-project-summary'
    | 'intake-user'
    | 'intake-client'
    | 'intake-operator';

export interface ContentBoundTextProvenance {
    version?: 1;
    source: JobDescriptionProvenanceSource | string;
    contentFingerprint?: string;
    /** Legacy compatibility for provenance written before fingerprints. */
    value?: string;
}

export interface VerifiedRfqContentProvenance {
    version: 1;
    source: 'verified-rfq-document';
    sourceId: string;
    sourceName: string;
    classification: 'rfq';
    classificationSource: string;
    contentFingerprint: string;
    sourceContentFingerprint: string;
    sourceContentLength: number;
    sourcePageCount: number | null;
    extractedPageCount: number | null;
    contentComplete: true;
    contentTruncated: false;
    provenanceFingerprint: string;
    match: 'exact' | 'source-contains-content';
}

export interface VerifiedRequestedScope {
    version: 1;
    source: 'verified-rfq-document';
    sourceId: string;
    sourceName: string;
    classification: 'rfq';
    rfqContentFingerprint: string;
    sourceContentFingerprint: string;
    provenanceFingerprint: string;
    contentFingerprint: string;
    rows: string[];
}

export interface ResolvedVerifiedRfqScope {
    provenance: VerifiedRfqContentProvenance;
    requestedScope: VerifiedRequestedScope;
    rows: string[];
}

export interface IncorporatedScopeDocumentV1 {
    version: 1;
    source: 'verified-rfq-incorporated-document';
    /** Technical/method authority only. The RFQ remains the commercial scope authority. */
    scopeAuthority: 'incorporated-technical-authority';
    sourceId: string;
    sourceName: string;
    classification: 'plan' | 'report' | 'scope_of_works';
    classificationSource: string;
    sourceContentFingerprint: string;
    rfqSourceId: string;
    rfqContentFingerprint: string;
    referenceStatement: string;
    referenceStatementFingerprint: string;
    relationshipFingerprint: string;
    sourceContentLength: number;
    sourcePageCount: number | null;
    extractedPageCount: number | null;
    contentComplete: true;
    contentTruncated: false;
    /** Full current source text. A downstream snapshot owns bounded projection/fail-closed coverage. */
    content: string;
}

export interface IncorporatedScopeRelationshipHoldV1 {
    version: 1;
    code:
        | 'incorporation-relationship-unresolved'
        | 'incorporation-relationship-ambiguous'
        | 'incorporation-source-limit-exceeded';
    referenceStatement: string;
    referenceStatementFingerprint: string;
    compatibleCandidateIds: string[];
}

export interface IncorporatedTechnicalAuthorityResolutionV1 {
    version: 1;
    authority: 'incorporated-technical-authority';
    complete: boolean;
    documents: IncorporatedScopeDocumentV1[];
    holds: IncorporatedScopeRelationshipHoldV1[];
}

const EXPLICIT_SCOPE_ROW = /^\s*(?:[-*\u2022]|\d+[.)]|[A-Za-z][.)])\s+(.+)$/;

// These are explicit client-request fields used by the frozen RFQ formats.
// They are deliberately bounded by the next routing/legal heading so contact,
// invoicing, and standard terms cannot inherit requested-work authority.
const SOURCE_BOUND_SCOPE_SECTIONS = [
    /\bSpecial Instructions for Worksite\b\s*([\s\S]*?)(?=\bContact Us\b|\bThank you for your services\b|$)/i,
    /\bDescription of work\b\s*:?\s*([\s\S]*?)(?=\bContact Us\b|\bThank you for your services\b|$)/i,
    /\bJob Description\b\s*:?\s*([\s\S]*?)(?=\bJob Issued By\b|\(VIEW ATTACHMENTS\)|\bDisclaimer\s*:|\bPlease Note\s*:|\bSupplier Contact\s*:|\bPlease email your invoice\b|\bQuote Requests\b|$)/i,
];

const SOURCE_WORK_REQUEST_START = /\b(?:on behalf of\b(?=[\s\S]{0,180}\b(?:please|can we)\b)|(?:the\s+)?owners?\b(?=[\s\S]{0,160}\b(?:is|are)\s+seeking\s+(?:a\s+)?quotes?\s+to\b)|we would be obliged\b|we (?:request|require|ask)\b|could you\b|please\b|can we\b|kindly\b)/i;
const SOURCE_WORK_REQUEST_VERB = /\b(?:attend|investigate|inspect|assess|advise|provide|proivde|supply|repair|rectify|replace|remove|install|quote|report|make good|reinstate|seal|waterproof|render|paint)\b/i;
const NON_WORK_REQUEST = /\b(?:unable to provide a quote|notify our office|forward (?:all correspondence|your quote)|email (?:your )?(?:invoice|quote)|contact (?:the|our|your|site)|invoice|report attached|see attached|click here|schedule inspection)\b/i;

// Layout-aware PDF extraction commonly represents contact and billing tables
// as Markdown bullets. Those rows are evidence, but they are not requested
// work. Treating them as explicit scope turns every address/phone/email field
// into a priced Stage 4 line. Keep this deny-list deliberately semantic and
// narrow: genuine rows such as "Drainage: install a grated channel" remain
// valid while administrative labels cannot acquire billing authority.
const ADMINISTRATIVE_SCOPE_LABEL = new RegExp(
    '^\\s*(?:\\*{1,2}|_{1,2})?\\s*'
    + '(?:contact(?:\\s+(?:name|mobile|phone|email))?'
    + '|bill\\s+to'
    + '|strata\\s+plan(?:\\s+(?:no|number))?'
    + '|the\\s+owners(?:\\s*,?\\s*strata\\s+plan(?:\\s+(?:no|number))?)?'
    + '|building\\s+name'
    + '|site\\s+contact(?:\\s+person)?'
    + '|agent(?:\\s*[/&-]\\s*manager)?'
    + '|w\\.?\\s*o\\.?\\s+issued\\s+by'
    + '|work\\s+order(?:\\s+(?:no|number|date))?'
    + '|issued\\s+to'
    + '|prepared\\s+for'
    + '|attention'
    + '|quote(?:\\s+request)?(?:\\s+(?:no|number))?'
    + '|priority'
    + '|date'
    + '|unit(?:\\s*[/&-]\\s*lot)?'
    + '|client'
    + '|address'
    + '|phone'
    + '|mobile'
    + '|fax'
    + '|email)'
    + '\\s*(?:\\*{1,2}|_{1,2})?\\s*:',
    'i',
);

export function isAdministrativeScopeRow(value: unknown): boolean {
    return ADMINISTRATIVE_SCOPE_LABEL.test(normaliseProvenanceText(value));
}

function normaliseRequestedScopeRows(rows: readonly unknown[]): string[] {
    const unique: string[] = [];
    const seen = new Set<string>();
    for (const raw of rows) {
        const row = normaliseProvenanceText(raw);
        if (row.length < 8 || isAdministrativeScopeRow(row)) continue;
        const key = comparableSourceText(row);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        unique.push(row);
    }
    return unique;
}

export function normaliseProvenanceText(value: unknown): string {
    if (value == null) return '';
    return String(value)
        .normalize('NFKC')
        .replace(/\r\n?/g, '\n')
        .replace(/[\t\f\v ]+/g, ' ')
        .replace(/ *\n */g, '\n')
        .trim();
}

function comparableSourceText(value: unknown): string {
    return normaliseProvenanceText(value).replace(/\s+/g, ' ').toLocaleLowerCase();
}

/**
 * Deterministic content identity for persisted provenance. This is a checksum,
 * not a security signature; it prevents stale or adjacent text from silently
 * inheriting authority after an edit or reload.
 */
export function fingerprintSourceContent(value: unknown): string {
    const text = normaliseProvenanceText(value);
    let first = 0x811c9dc5;
    let second = 0x9e3779b9;
    for (let index = 0; index < text.length; index += 1) {
        const code = text.charCodeAt(index);
        first ^= code;
        first = Math.imul(first, 0x01000193);
        second ^= code + index;
        second = Math.imul(second, 0x85ebca6b);
        second ^= second >>> 13;
    }
    return `qm-content-v1:${(first >>> 0).toString(16).padStart(8, '0')}${(second >>> 0).toString(16).padStart(8, '0')}`;
}

export function createTextProvenance(
    source: JobDescriptionProvenanceSource,
    content: unknown,
): ContentBoundTextProvenance {
    const value = normaliseProvenanceText(content);
    return {
        version: 1,
        source,
        contentFingerprint: fingerprintSourceContent(value),
        value,
    };
}

export function contentProvenanceMatches(
    provenance: unknown,
    content: unknown,
): boolean {
    if (!provenance || typeof provenance !== 'object') return false;
    const candidate = provenance as LooseRecord;
    const current = normaliseProvenanceText(content);
    if (!current) return false;
    if (typeof candidate.contentFingerprint === 'string' && candidate.contentFingerprint) {
        return candidate.contentFingerprint === fingerprintSourceContent(current);
    }
    return typeof candidate.value === 'string'
        && normaliseProvenanceText(candidate.value) === current;
}

function documentClassification(document: LooseRecord): string {
    return String(resolveDocumentClassification(document).classification || 'other');
}

function classificationSource(document: LooseRecord): string {
    return String(resolveDocumentClassification(document).source || 'persisted-document-type');
}

function rfqProvenanceFingerprint(provenance: Omit<VerifiedRfqContentProvenance, 'provenanceFingerprint'>): string {
    return fingerprintSourceContent(JSON.stringify({
        version: provenance.version,
        source: provenance.source,
        sourceId: provenance.sourceId,
        sourceName: provenance.sourceName,
        classification: provenance.classification,
        classificationSource: provenance.classificationSource,
        contentFingerprint: provenance.contentFingerprint,
        sourceContentFingerprint: provenance.sourceContentFingerprint,
        sourceContentLength: provenance.sourceContentLength,
        sourcePageCount: provenance.sourcePageCount,
        extractedPageCount: provenance.extractedPageCount,
        contentComplete: provenance.contentComplete,
        contentTruncated: provenance.contentTruncated,
        match: provenance.match,
    }));
}

interface MatchedPreprocessedSource {
    candidate: LooseRecord;
    text: string;
}

function matchingPreprocessedSource(
    document: LooseRecord,
    preprocessedFiles: readonly unknown[],
): MatchedPreprocessedSource | null {
    const documentId = String(document.id || '').trim();
    const documentName = String(document.name || document.originalName || '').trim().toLocaleLowerCase();
    const rows: MatchedPreprocessedSource[] = [];
    for (const raw of preprocessedFiles) {
        if (!raw || typeof raw !== 'object') continue;
        const candidate = raw as LooseRecord;
        const candidateId = String(candidate.id || '').trim();
        const candidateName = String(candidate.originalName || candidate.name || '').trim().toLocaleLowerCase();
        const exactId = documentId && candidateId === documentId;
        const sameName = documentName && candidateName === documentName;
        if (!exactId && !sameName) continue;
        const text = normaliseProvenanceText(candidate.text || candidate.content);
        if (text) rows.push({ candidate, text });
    }
    if (documentId) {
        const exactIdRows = rows.filter(({ candidate }) => String(candidate.id || '').trim() === documentId);
        if (exactIdRows.length === 1) return exactIdRows[0] || null;
        if (exactIdRows.length > 1) return null;
    }
    // Preprocessing rows and Stage 1 document rows deliberately have separate
    // IDs. A filename alone is still not identity: reload/import paths may
    // contain duplicates, so the fallback requires a unique name plus matching
    // byte size or content checksum.
    const documentSize = Number(document.size || document.fileSize || document.sizeBytes || 0);
    const documentFingerprint = String(
        document.contentFingerprint || document.sourceContentFingerprint || document.sha256 || '',
    ).trim();
    const qualified = rows.filter(({ candidate, text }) => {
        const candidateName = String(candidate.originalName || candidate.name || '').trim().toLocaleLowerCase();
        if (!documentName || candidateName !== documentName) return false;
        const candidateSize = Number(candidate.size || candidate.fileSize || candidate.sizeBytes || 0);
        const candidateFingerprint = String(
            candidate.contentFingerprint || candidate.sourceContentFingerprint || candidate.sha256 || '',
        ).trim();
        return (documentSize > 0 && candidateSize === documentSize)
            || (documentFingerprint && (
                candidateFingerprint === documentFingerprint
                || fingerprintSourceContent(text) === documentFingerprint
            ));
    });
    return qualified.length === 1 ? qualified[0] || null : null;
}

function completePreprocessedExtraction(candidate: LooseRecord): boolean {
    const metadata = candidate.metadata && typeof candidate.metadata === 'object'
        ? candidate.metadata as LooseRecord
        : null;
    const pdfEvidence = metadata?.pdfEvidence && typeof metadata.pdfEvidence === 'object'
        ? metadata.pdfEvidence as LooseRecord
        : null;
    const pageCount = positivePageCount(candidate.pageCount || metadata?.pageCount || pdfEvidence?.totalPages);
    const evidencePages = Array.isArray(pdfEvidence?.pages)
        ? new Set(pdfEvidence.pages
            .map(row => positivePageCount((row as LooseRecord)?.page))
            .filter((page): page is number => page !== null)).size
        : null;
    const extractedPageCount = positivePageCount(
        candidate.extractedPageCount
        || metadata?.extractedPageCount
        || pdfEvidence?.extractedPageCount
        || evidencePages,
    );
    const explicitlyComplete = candidate.contentComplete === true
        || metadata?.contentComplete === true
        || pdfEvidence?.contentComplete === true;
    const explicitlyTruncated = candidate.contentTruncated === true
        || metadata?.contentTruncated === true
        || pdfEvidence?.contentTruncated === true;
    const hasErrors = Array.isArray(candidate.errors) && candidate.errors.length > 0;
    const coverageComplete = pageCount === null
        || (extractedPageCount !== null && extractedPageCount >= pageCount);
    return explicitlyComplete && !explicitlyTruncated && !hasErrors && coverageComplete;
}

const INCORPORATED_DOCUMENT_CLASSES = new Set(['plan', 'report', 'scope_of_works']);
const INCORPORATION_CUE = /\b(?:attach(?:ed|ments?)?|incorporat(?:e|ed|ing)|in\s+accordance\s+with|as\s+per|refer(?:ring)?\s+to|forms?\s+part\s+of|must\s+comply\s+with)\b/i;
const INCORPORATION_DOCUMENT_NOUN = /\b(?:regulated\s+design|designs?|drawings?|plans?|specifications?|scope(?:\s+of\s+works?)?|details?|schedules?|reports?|declarations?|documents?)\b/i;
const STRONG_INCORPORATION_DOCUMENT_NOUN = /\b(?:regulated\s+design|designs?|drawings?|plans?|specifications?|scope(?:\s+of\s+works?)?|schedules?|reports?|declarations?)\b/i;
const OPERATIONAL_SAFETY_REGISTER_REFERENCE = /\b(?:asbestos|hazardous\s+materials?)\s+register\b/i;
const OPERATIONAL_REGISTER_LOCATION_OR_TIMING = /\b(?:document\s+box|box\s+on\s+site|before\s+undertaking\b[\s\S]{0,80}\b(?:work|inspection))\b/i;
const STRONG_NORMATIVE_INCORPORATION_CUE = /\b(?:attach(?:ed|ment)?|incorporat(?:e|ed|ing)|in\s+accordance\s+with|as\s+per|forms?\s+part\s+of|must\s+comply\s+with)\b/i;
// These are document-delivery/interface actions, not statements that grant an
// attachment technical authority. Waratah's punctuation-poor PDF extraction
// can otherwise place `ATTACH QUOTE`, `Building Documents`, and the actual Job
// Description in one enormous clause and falsely bind those unrelated labels.
const NON_AUTHORITY_ATTACHMENT_CUE = /\b(?:click\s+here\s+to\s+)?attach\s+quote\b|\bsee\s+(?:the\s+)?attached\s+(?:images?|photos?)\s*(?=[.!?]|$)/gi;
// A client saying that an investigator's report is available is evidence
// routing, not an instruction to build the repair to that report. Keep this
// deliberately sentence-bounded and limited to `find/see ... report attached`
// so normative wording such as `in accordance with the attached report`
// remains incorporated technical authority.
const BARE_REPORT_AVAILABILITY_CUE = /^\s*(?:please\s+)?(?:find|see)\b[^.!?\n]{0,180}\breports?\s+(?:is\s+|are\s+)?attached\s*[.!?]?\s*$/i;
const PARENTHESIZED_VIEW_ATTACHMENT_CUE = /\(\s*view\s+attachments?\s*\)/gi;
const PARENTHESIZED_PHOTO_ATTACHMENT_CUE = /\(\s*photos?\s+attached\s*\)/gi;
const INCORPORATION_CONTINUATION_START = new RegExp(
    `^(?:(?:are|is|was|were|be|been|being)\\s+|(?:have|has|had)(?:\\s+been)?\\s+)?${INCORPORATION_CUE.source}`,
    'i',
);
const ADJACENT_TECHNICAL_COREFERENCE_START = new RegExp(
    `^(?:(?:it|this|that|these|those|they)\\s+(?:(?:is|are|was|were|has|have|had)(?:\\s+been)?\\s+)?${INCORPORATION_CUE.source}|(?:please\\s+)?refer(?:ring)?\\s+to\\s+(?:the\\s+)?attach(?:ments?)?\\b)`,
    'i',
);
const INCORPORATION_ANCHOR_STOP_WORDS = new Set([
    'about', 'accordance', 'already', 'also', 'amend', 'appointed', 'are', 'as', 'attach', 'attached',
    'attachment', 'attachments', 'been', 'better', 'building', 'client', 'comply', 'considered', 'design',
    'designs', 'detail', 'details', 'document', 'documents', 'drawing', 'drawings', 'engineer', 'engineers', 'exterior',
    'following', 'for', 'forms', 'had', 'happy', 'has', 'have', 'incorporated', 'its', 'job', 'must', 'our', 'part',
    'plan', 'plans', 'please', 'price', 'property', 'provide', 'quote', 'quotes', 'refer', 'regulated',
    'report', 'reports', 'request', 'scope', 'seeking', 'specification', 'specifications', 'supplied',
    'terrace', 'the', 'these', 'their', 'this', 'under', 'unit', 'use', 'used', 'using', 'view', 'with',
    'waterproofing', 'works', 'would',
]);

function stripNonAuthorityAttachmentCues(value: string): string {
    const withoutDeliveryActions = BARE_REPORT_AVAILABILITY_CUE.test(value)
        ? ' '
        : value.replace(NON_AUTHORITY_ATTACHMENT_CUE, ' ');
    const preserveNamedLocalAttachment = (
        token: string,
        offset: number,
        sourceText: string,
    ): string => {
            // Keep a parenthesised link when its own punctuation-bounded label
            // names a technical source, e.g. `(VIEW ATTACHMENTS) NJK PLAN` or
            // `NJK specifications (Photo Attached)`. Bound the context tightly
            // so distant portal headings cannot pair with a standalone token.
            const before = sourceText.slice(0, offset).split(/[\n.!?]/).pop() || '';
            const after = sourceText.slice(offset + token.length).split(/[\n.!?]/)[0] || '';
            const localLabel = `${before.slice(-96)} ${after.slice(0, 96)}`;
            const hasStrongTechnicalNoun = STRONG_INCORPORATION_DOCUMENT_NOUN.test(localLabel);
            const hasTechnicalNoun = INCORPORATION_DOCUMENT_NOUN.test(localLabel);
            const hasDistinctiveAnchor = incorporationReferenceAnchors(localLabel).length > 0;
            return hasStrongTechnicalNoun || (hasTechnicalNoun && hasDistinctiveAnchor)
                ? token
                : ' ';
        };
    return withoutDeliveryActions
        .replace(PARENTHESIZED_PHOTO_ATTACHMENT_CUE, preserveNamedLocalAttachment)
        .replace(PARENTHESIZED_VIEW_ATTACHMENT_CUE, preserveNamedLocalAttachment);
}

function incorporatedReferenceStatements(rfqContent: unknown): string[] {
    const content = normaliseProvenanceText(rfqContent);
    if (!content) return [];
    // PDF text extraction commonly inserts a hard newline inside a prose
    // sentence. Reflow only obvious continuation lines; list/table boundaries
    // remain intact. The live Cremorne RFQ, for example, wraps `are` and
    // `attached.` onto separate lines.
    const reflowed = content
        // An explicit unit identifier that PDF layout wrapped across lines
        // (`Unit\n21`, `Unit No.\n21`, `Unit\nNo. 21`) is one identifier. Join it
        // before any splitting, so the unit stays in the clause that carries the
        // document noun and cue, and the later canonicalisation can see it whole.
        .replace(/(\bunit(?:[ \t]*(?:no\.?|number|#))?)[ \t]*\n[ \t]*(?=\d)/gi, '$1 ')
        .replace(/(\bunit)[ \t]*\n[ \t]*(?=(?:no\.?|number|#)[ \t]*\d)/gi, '$1 ')
        .replace(/([^\n.!?])\n(?=[a-z0-9("'’])/g, '$1 ')
        // Uppercase PDF layout can wrap the predicate onto the next line,
        // e.g. `NJK'S SPECIFICATIONS\nARE ATTACHED.`. Reflow only a directly
        // adjacent line whose predecessor already names a technical-document
        // class and whose continuation begins with an incorporation cue. This
        // preserves the local relationship without reviving whole-RFQ pairing.
        .replace(/([^\n.!?]+)\n(?=([^\n.!?]+))/g, (boundary, previousLine, nextLine) => {
            const previousAuthority = stripNonAuthorityAttachmentCues(previousLine);
            const nextAuthority = stripNonAuthorityAttachmentCues(nextLine).trim();
            return INCORPORATION_DOCUMENT_NOUN.test(previousAuthority)
                && INCORPORATION_CONTINUATION_START.test(nextAuthority)
                ? `${previousLine} `
                : boundary;
        });
    const clauses = reflowed
        // The abbreviation `No.` before a number (`Unit No. 21`, `Report No. 5`)
        // is not a sentence end: splitting there strands the identifier in its own
        // clause, away from the document noun and cue that make it a reference.
        .split(/(?<=[.!?])(?<!\b(?:no|nos)\.(?=\s+\d))\s+|\n+/i)
        .map(value => value.replace(/\s+/g, ' ').trim())
        .filter(Boolean);
    const relationshipClauses: string[] = [];
    for (let index = 0; index < clauses.length; index += 1) {
        const clause = clauses[index];
        if (!clause) continue;
        relationshipClauses.push(clause);
        const nextClause = clauses[index + 1];
        if (!nextClause || nextClause.length > 160) continue;
        const previousAuthority = stripNonAuthorityAttachmentCues(clause);
        const nextAuthority = stripNonAuthorityAttachmentCues(nextClause).trim();
        const priorNamesStrongTechnicalSource = STRONG_INCORPORATION_DOCUMENT_NOUN.test(previousAuthority)
            && incorporationReferenceAnchors(previousAuthority).length > 0;
        const priorAlreadyIncorporates = INCORPORATION_CUE.test(previousAuthority);
        if (priorNamesStrongTechnicalSource
            && !priorAlreadyIncorporates
            && ADJACENT_TECHNICAL_COREFERENCE_START.test(nextAuthority)) {
            // A short immediately-adjacent sentence may carry the relationship
            // predicate for the named technical source in the prior sentence.
            // Never search beyond this one-clause boundary.
            relationshipClauses.push(`${clause} ${nextClause}`);
        }
    }
    const selected: string[] = [];
    const seen = new Set<string>();
    for (const clause of relationshipClauses) {
        const authorityClause = stripNonAuthorityAttachmentCues(clause);
        if (!INCORPORATION_CUE.test(authorityClause)
            || !INCORPORATION_DOCUMENT_NOUN.test(authorityClause)) continue;
        const key = comparableSourceText(clause);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        selected.push(clause);
    }
    return selected;
}

function incorporationReferenceAnchors(statement: string): string[] {
    const normalisedStatement = canonicaliseUnitIdentifiers(statement.normalize('NFKC'));
    const rawTokens = normalisedStatement.match(/[A-Za-z0-9][A-Za-z0-9&._/-]{1,}/g) || [];
    const unique = new Set<string>();
    for (const raw of rawTokens) {
        const value = raw
            .toLocaleLowerCase()
            .replace(/(?:['’]s)$/i, '')
            .replace(/[^a-z0-9]+/g, '');
        const isAcronym = /^[A-Z][A-Z0-9]{1,}$/.test(raw.replace(/[^A-Za-z0-9]/g, ''));
        const hasDigit = /\d/.test(value);
        const isPossessiveName = new RegExp(`\\b${raw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[’']s\\b`, 'i')
            .test(normalisedStatement);
        if ((!isAcronym && !hasDigit && !isPossessiveName)
            || value.length < 2
            || INCORPORATION_ANCHOR_STOP_WORDS.has(value)) continue;
        unique.add(value);
    }
    return [...unique].slice(0, 16);
}

// An explicit unit identifier in any of the ways it is written: `Unit 21`,
// `Unit21`, `U21`, `Unit No. 21`, `unit #21`, `unit-21`. The compact `u` form
// needs the digits attached, so a stray `u` before a number is not read as one.
const EXPLICIT_UNIT_IDENTIFIER = /\b(?:unit(?:\s*(?:no\.?|number|#))?[\s.:#-]*|u(?=\d))(\d+[a-z]?)\b/gi;

/**
 * Write every explicit unit identifier as ONE token (`unit21`), on whichever
 * side of the comparison it appears. The RFQ's reference sentence said `U21`
 * while the report said `Unit 21`: two spellings of one unit were two different
 * anchors, so the report never bound (RFQ 903025, 2026-10-18). The same step
 * closes the opposite hole: a unit's digits used to survive as a bare `21`, so
 * an unrelated `Page 21` in a report about Unit 22 satisfied "Unit 21". After
 * this, a unit number only ever equals the same unit number, and only a unit
 * identifier can carry one. Numbers that are not unit identifiers (a report
 * reference, a plan number) are untouched and still match as before.
 */
function canonicaliseUnitIdentifiers(text: string): string {
    return text.replace(EXPLICIT_UNIT_IDENTIFIER, (_match, identifier: string) => ` unit${identifier.toLocaleLowerCase()} `);
}

function candidateTokenSet(name: string, content: string): Set<string> {
    return new Set(canonicaliseUnitIdentifiers(`${name}\n${content}`.normalize('NFKC'))
        .toLocaleLowerCase()
        .match(/[a-z0-9]+/g) || []);
}

const COMPANY_TOKEN_SUFFIX = /^(?:consulting|consultants?|engineering|engineers?|design|designs|group|associates?|architects?)$/;

function candidateContainsIncorporationAnchor(candidateTokens: Set<string>, anchor: string): boolean {
    if (candidateTokens.has(anchor)) return true;
    // Real PDF extraction can omit a logo/company heading while preserving an
    // email domain such as `info@njkconsulting.com.au`. Permit only a bounded
    // company-suffix continuation of a distinctive 3+ character anchor; never
    // generic substring matching (`njk` may match `njkconsulting`, but `eng`
    // does not match arbitrary longer words).
    if (!/^[a-z0-9]{3,}$/.test(anchor)) return false;
    return [...candidateTokens].some(token => (
        token.startsWith(anchor)
        && token.length > anchor.length
        && COMPANY_TOKEN_SUFFIX.test(token.slice(anchor.length))
    ));
}

function compatibleClassesForStatement(statement: string): Set<string> {
    if (/\b(?:scope(?:\s+of\s+works?)?)\b/i.test(statement)) {
        return new Set(['scope_of_works']);
    }
    if (/\b(?:declaration|compliance\s+declaration|report)s?\b/i.test(statement)) {
        return new Set(['report']);
    }
    if (/\b(?:regulated\s+design|designs?|drawings?|plans?|specifications?|details?|schedules?)\b/i.test(statement)) {
        return new Set(['plan', 'scope_of_works']);
    }
    return new Set();
}

function exactNamedDocumentMatch(statement: string, name: string): boolean {
    const statementText = comparableSourceText(statement).replace(/\b(?:the|an?|attached|attachment)\b/g, ' ');
    const stem = comparableSourceText(name).replace(/\.[a-z0-9]{2,5}$/i, '').replace(/\s+/g, ' ').trim();
    if (stem.length < 8) return false;
    return statementText.includes(stem);
}

function relationshipFingerprint(
    row: Omit<IncorporatedScopeDocumentV1, 'relationshipFingerprint' | 'content'>,
): string {
    return fingerprintSourceContent(JSON.stringify(row));
}

/**
 * Resolve documents that a content-bound, classified RFQ expressly incorporates.
 *
 * A nearby upload does not become billable authority merely because it is a plan.
 * The RFQ must contain an incorporation statement and that statement must carry a
 * distinctive anchor (for example the named designer) found in the candidate's
 * current filename/content. The only anchor-free exception is one unambiguous,
 * content-classified Scope of Works explicitly named by the RFQ.
 */
export function deriveIncorporatedScopeDocuments({
    formData = {},
    documents = [],
    preprocessedFiles = [],
}: {
    formData?: LooseRecord;
    documents?: readonly unknown[];
    preprocessedFiles?: readonly unknown[];
} = {}): IncorporatedScopeDocumentV1[] {
    const resolution = resolveIncorporatedTechnicalAuthority({ formData, documents, preprocessedFiles });
    return resolution.complete ? resolution.documents : [];
}

function positivePageCount(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function completeDocumentExtraction(document: LooseRecord): {
    complete: boolean;
    pageCount: number | null;
    extractedPageCount: number | null;
} {
    const pdfEvidence = document.pdfEvidence && typeof document.pdfEvidence === 'object'
        ? document.pdfEvidence as LooseRecord
        : null;
    const pageCount = positivePageCount(document.pageCount || pdfEvidence?.totalPages);
    const evidencePages = Array.isArray(pdfEvidence?.pages)
        ? new Set(pdfEvidence.pages
            .map(row => positivePageCount((row as LooseRecord)?.page))
            .filter((page): page is number => page !== null)).size
        : null;
    const extractedPageCount = positivePageCount(
        document.extractedPageCount
        || pdfEvidence?.extractedPageCount
        || evidencePages,
    );
    const explicitlyComplete = document.contentComplete === true
        || document.extractionComplete === true;
    const explicitlyTruncated = document.contentTruncated === true
        || document.extractionTruncated === true;
    const coverageComplete = pageCount === null
        || (extractedPageCount !== null && extractedPageCount >= pageCount);
    return {
        complete: explicitlyComplete
            && !explicitlyTruncated
            && document.extractionFailed !== true
            && coverageComplete,
        pageCount,
        extractedPageCount,
    };
}

/**
 * Resolve the RFQ -> technical-document relationship and surface ambiguity as
 * a mandatory builder-review hold. A generic attachment phrase is never enough
 * to grant technical or commercial authority.
 */
export function resolveIncorporatedTechnicalAuthority({
    formData = {},
    documents = [],
    preprocessedFiles = [],
}: {
    formData?: LooseRecord;
    documents?: readonly unknown[];
    preprocessedFiles?: readonly unknown[];
} = {}): IncorporatedTechnicalAuthorityResolutionV1 {
    const rfqContent = formData?.rfqContent;
    const rawProvenance = formData?.rfqContentProvenance;
    const empty = (holds: IncorporatedScopeRelationshipHoldV1[] = []): IncorporatedTechnicalAuthorityResolutionV1 => ({
        version: 1,
        authority: 'incorporated-technical-authority',
        complete: holds.length === 0,
        documents: [],
        holds,
    });
    const rfqReferenceHold = (): IncorporatedScopeRelationshipHoldV1 => {
        const referenceStatement = normaliseProvenanceText(rfqContent).slice(0, 800);
        return {
            version: 1,
            code: 'incorporation-relationship-unresolved',
            referenceStatement,
            referenceStatementFingerprint: fingerprintSourceContent(referenceStatement),
            compatibleCandidateIds: [],
        };
    };
    const statements = incorporatedReferenceStatements(rfqContent);
    const rfqMentionsTechnicalSource = statements.length > 0;
    if (!isVerifiedRfqContentProvenance(rawProvenance, rfqContent)) {
        return rfqMentionsTechnicalSource ? empty([rfqReferenceHold()]) : empty();
    }
    // A persisted provenance receipt is not proof that its RFQ is still in the
    // project. Re-derive from the current document set so removal,
    // reclassification, incomplete extraction, or content replacement cannot
    // leave technical authority alive after reload.
    const currentProvenance = deriveVerifiedRfqContentProvenance({
        rfqContent,
        documents,
        preprocessedFiles,
    });
    if (!currentProvenance
        || currentProvenance.provenanceFingerprint !== rawProvenance.provenanceFingerprint) {
        return rfqMentionsTechnicalSource ? empty([rfqReferenceHold()]) : empty();
    }
    const provenance = rawProvenance;
    if (statements.length === 0) {
        return empty();
    }

    const candidates = documents.flatMap((raw): Array<{
        document: LooseRecord;
        content: string;
        classification: IncorporatedScopeDocumentV1['classification'];
        classificationSource: string;
        pageCount: number | null;
        extractedPageCount: number | null;
    }> => {
        if (!raw || typeof raw !== 'object') return [];
        const document = raw as LooseRecord;
        const resolved = resolveDocumentClassification(document);
        const classification = String(resolved.classification || 'other');
        if (!INCORPORATED_DOCUMENT_CLASSES.has(classification)) return [];
        // Raw type fields, filename guesses and stale confidence/evidence are
        // context only. Technical authority requires explicit trusted provenance.
        const contentAwareBatch = resolved.source === 'ai-batch-classifier-content'
            && Number(document.aiClassificationConfidence || 0) >= 90;
        if (!isTrustedDocumentClassSource(resolved.source) && !contentAwareBatch) return [];
        const embeddedContent = normaliseProvenanceText(document.content);
        const preprocessedSource = embeddedContent
            ? null
            : matchingPreprocessedSource(document, preprocessedFiles);
        if (!embeddedContent && (!preprocessedSource
            || !completePreprocessedExtraction(preprocessedSource.candidate))) return [];
        const content = embeddedContent || preprocessedSource?.text || '';
        if (content.length < 40) return [];
        const extraction = completeDocumentExtraction(document);
        if (!extraction.complete) return [];
        return [{
            document,
            content,
            classification: classification as IncorporatedScopeDocumentV1['classification'],
            classificationSource: String(resolved.source || 'persisted-document-type'),
            pageCount: extraction.pageCount,
            extractedPageCount: extraction.extractedPageCount,
        }];
    });

    const matched = new Map<string, IncorporatedScopeDocumentV1>();
    const holds: IncorporatedScopeRelationshipHoldV1[] = [];
    for (const statement of statements) {
        const anchors = incorporationReferenceAnchors(statement);
        const compatibleClasses = compatibleClassesForStatement(statement);
        // An on-site Asbestos/Hazardous Materials Register is a safety/access
        // instruction, not an attempt to incorporate a plan, report or Scope of
        // Works into the quoted repair. Keep this exception narrow: an otherwise
        // unidentified "attached document" still fails closed below.
        const isOperationalSafetyRegister = compatibleClasses.size === 0
            && OPERATIONAL_SAFETY_REGISTER_REFERENCE.test(statement)
            && OPERATIONAL_REGISTER_LOCATION_OR_TIMING.test(statement)
            && !STRONG_NORMATIVE_INCORPORATION_CUE.test(statement);
        if (isOperationalSafetyRegister) continue;
        const compatibleCandidates = candidates.filter(candidate => compatibleClasses.has(candidate.classification));
        const relationshipMatches = compatibleCandidates.filter(candidate => {
            const name = String(candidate.document.name || candidate.document.originalName || '').trim();
            const candidateTokens = candidateTokenSet(name, candidate.content);
            return exactNamedDocumentMatch(statement, name)
                || (anchors.length > 0 && anchors.every(anchor => (
                    candidateContainsIncorporationAnchor(candidateTokens, anchor)
                )));
        });
        if (relationshipMatches.length !== 1) {
            holds.push({
                version: 1,
                code: relationshipMatches.length > 1
                    ? 'incorporation-relationship-ambiguous'
                    : 'incorporation-relationship-unresolved',
                referenceStatement: statement,
                referenceStatementFingerprint: fingerprintSourceContent(statement),
                compatibleCandidateIds: relationshipMatches.map(candidate => {
                    const name = String(candidate.document.name || candidate.document.originalName || '').trim();
                    return String(candidate.document.id || '').trim()
                        || `document:${fingerprintSourceContent(name)}`;
                }).sort(),
            });
            continue;
        }
        for (const candidate of relationshipMatches) {
            const name = String(candidate.document.name || candidate.document.originalName || '').trim();
            const sourceFingerprint = fingerprintSourceContent(candidate.content);
            const sourceId = String(candidate.document.id || '').trim()
                || `document:${fingerprintSourceContent(`${name}\n${sourceFingerprint}`)}`;
            if (sourceId === provenance.sourceId) continue;
            const sourceName = name || 'Incorporated scope document';
            const referenceStatement = statement.slice(0, 800);
            const referenceStatementFingerprint = fingerprintSourceContent(referenceStatement);
            const withoutRelationship: Omit<IncorporatedScopeDocumentV1, 'relationshipFingerprint' | 'content'> = {
                version: 1,
                source: 'verified-rfq-incorporated-document',
                scopeAuthority: 'incorporated-technical-authority',
                sourceId,
                sourceName,
                classification: candidate.classification,
                classificationSource: candidate.classificationSource,
                sourceContentFingerprint: sourceFingerprint,
                rfqSourceId: provenance.sourceId,
                rfqContentFingerprint: provenance.contentFingerprint,
                referenceStatement,
                referenceStatementFingerprint,
                sourceContentLength: candidate.content.length,
                sourcePageCount: candidate.pageCount,
                extractedPageCount: candidate.extractedPageCount,
                contentComplete: true,
                contentTruncated: false,
            };
            matched.set(sourceId, {
                ...withoutRelationship,
                relationshipFingerprint: relationshipFingerprint(withoutRelationship),
                content: candidate.content,
            });
        }
    }
    const resolvedDocuments = [...matched.values()]
        .sort((left, right) => left.sourceId.localeCompare(right.sourceId));
    if (resolvedDocuments.length > INCORPORATED_AUTHORITY_LIMITS.maxSources) {
        const limitStatement = `RFQ resolves ${resolvedDocuments.length} incorporated technical sources; the bounded maximum is ${INCORPORATED_AUTHORITY_LIMITS.maxSources}.`;
        holds.push({
            version: 1,
            code: 'incorporation-source-limit-exceeded',
            referenceStatement: limitStatement,
            referenceStatementFingerprint: fingerprintSourceContent(limitStatement),
            compatibleCandidateIds: resolvedDocuments.map(document => document.sourceId),
        });
    }
    return {
        version: 1,
        authority: 'incorporated-technical-authority',
        complete: holds.length === 0,
        documents: resolvedDocuments,
        holds,
    };
}

export function deriveVerifiedRfqContentProvenance({
    rfqContent,
    documents = [],
    preprocessedFiles = [],
}: {
    rfqContent: unknown;
    documents?: readonly unknown[];
    preprocessedFiles?: readonly unknown[];
}): VerifiedRfqContentProvenance | null {
    const content = normaliseProvenanceText(rfqContent);
    const comparableContent = comparableSourceText(content);
    if (!content || comparableContent.length < 20) return null;

    const matches: Array<{
        document: LooseRecord;
        sourceText: string;
        match: VerifiedRfqContentProvenance['match'];
        sourcePageCount: number | null;
        extractedPageCount: number | null;
    }> = [];
    for (const raw of documents) {
        if (!raw || typeof raw !== 'object') continue;
        const document = raw as LooseRecord;
        const resolvedClassification = resolveDocumentClassification(document);
        if (String(resolvedClassification.classification || '') !== 'rfq') continue;
        const resolvedClassificationSource = String(resolvedClassification.source || '').trim();
        const contentAwareBatch = resolvedClassificationSource === 'ai-batch-classifier-content'
            && Number(document.aiClassificationConfidence || 0) >= 90;
        // A stale/raw persisted `type: rfq` is not source authority. Require a
        // deterministic content classifier, validated manifest, or explicit
        // operator/human confirmation before RFQ text can authorise scope.
        if (!isTrustedDocumentClassSource(resolvedClassificationSource) && !contentAwareBatch) continue;
        const extraction = completeDocumentExtraction(document);
        if (!extraction.complete) continue;
        const embeddedContent = normaliseProvenanceText(document.content);
        const preprocessedSource = embeddedContent
            ? null
            : matchingPreprocessedSource(document, preprocessedFiles);
        if (!embeddedContent && (!preprocessedSource
            || !completePreprocessedExtraction(preprocessedSource.candidate))) continue;
        const sourceText = embeddedContent || preprocessedSource?.text || '';
        const comparableSource = comparableSourceText(sourceText);
        if (!sourceText || !comparableSource) continue;
        if (comparableSource === comparableContent) {
            matches.push({
                document,
                sourceText,
                match: 'exact',
                sourcePageCount: extraction.pageCount,
                extractedPageCount: extraction.extractedPageCount,
            });
            continue;
        }
        const isSubstantialSourceExcerpt = comparableContent.length >= 40
            && comparableSource.includes(comparableContent)
            && comparableContent.length / comparableSource.length >= 0.2;
        if (isSubstantialSourceExcerpt) {
            matches.push({
                document,
                sourceText,
                match: 'source-contains-content',
                sourcePageCount: extraction.pageCount,
                extractedPageCount: extraction.extractedPageCount,
            });
        }
    }
    matches.sort((left, right) => {
        if (left.match !== right.match) return left.match === 'exact' ? -1 : 1;
        return left.sourceText.length - right.sourceText.length;
    });
    const selected = matches[0];
    if (!selected) return null;

    const sourceName = String(selected.document.name || selected.document.originalName || 'RFQ document').trim();
    const sourceFingerprint = fingerprintSourceContent(selected.sourceText);
    const sourceId = String(selected.document.id || '').trim()
        || `document:${fingerprintSourceContent(`${sourceName}\n${sourceFingerprint}`)}`;
    const provenanceWithoutFingerprint: Omit<VerifiedRfqContentProvenance, 'provenanceFingerprint'> = {
        version: 1,
        source: 'verified-rfq-document',
        sourceId,
        sourceName,
        classification: 'rfq',
        classificationSource: classificationSource(selected.document),
        contentFingerprint: fingerprintSourceContent(content),
        sourceContentFingerprint: sourceFingerprint,
        sourceContentLength: selected.sourceText.length,
        sourcePageCount: selected.sourcePageCount,
        extractedPageCount: selected.extractedPageCount,
        contentComplete: true,
        contentTruncated: false,
        match: selected.match,
    };
    return {
        ...provenanceWithoutFingerprint,
        provenanceFingerprint: rfqProvenanceFingerprint(provenanceWithoutFingerprint),
    };
}

export function isVerifiedRfqContentProvenance(
    provenance: unknown,
    rfqContent: unknown,
): provenance is VerifiedRfqContentProvenance {
    if (!provenance || typeof provenance !== 'object') return false;
    const candidate = provenance as LooseRecord;
    if (!(candidate.version === 1
        && candidate.source === 'verified-rfq-document'
        && candidate.classification === 'rfq'
        && typeof candidate.sourceId === 'string'
        && candidate.sourceId.trim().length > 0
        && typeof candidate.sourceName === 'string'
        && candidate.sourceName.trim().length > 0
        && typeof candidate.classificationSource === 'string'
        && candidate.classificationSource.trim().length > 0
        && (isTrustedDocumentClassSource(candidate.classificationSource)
            || candidate.classificationSource === 'ai-batch-classifier-content')
        && typeof candidate.sourceContentFingerprint === 'string'
        && candidate.sourceContentFingerprint.startsWith('qm-content-v1:')
        && Number.isInteger(candidate.sourceContentLength)
        && Number(candidate.sourceContentLength) > 0
        && (candidate.sourcePageCount === null
            || (Number.isInteger(candidate.sourcePageCount) && Number(candidate.sourcePageCount) > 0))
        && (candidate.extractedPageCount === null
            || (Number.isInteger(candidate.extractedPageCount) && Number(candidate.extractedPageCount) > 0))
        && candidate.contentComplete === true
        && candidate.contentTruncated === false
        && typeof candidate.provenanceFingerprint === 'string'
        && typeof candidate.contentFingerprint === 'string'
        && candidate.contentFingerprint === fingerprintSourceContent(rfqContent)
        && (candidate.match === 'exact' || candidate.match === 'source-contains-content'))) {
        return false;
    }
    const verified = candidate as unknown as VerifiedRfqContentProvenance;
    const { provenanceFingerprint, ...withoutFingerprint } = verified;
    return provenanceFingerprint === rfqProvenanceFingerprint(withoutFingerprint);
}

export function extractExplicitRequestedScopeRows(content: unknown): string[] {
    const source = normaliseProvenanceText(content);
    for (const sectionPattern of SOURCE_BOUND_SCOPE_SECTIONS) {
        const section = sectionPattern.exec(source)?.[1];
        if (!section) continue;
        const sectionRows = normaliseRequestedScopeRows(
            section
                .replace(/\s+/g, ' ')
                .split(/(?<=[.!?])\s+/)
                .map((sentence) => {
                    const requestStart = SOURCE_WORK_REQUEST_START.exec(sentence);
                    if (!requestStart) return '';
                    return sentence.slice(requestStart.index).trim();
                })
                .filter(row => (
                    row.length >= 12
                    && row.length <= 500
                    && SOURCE_WORK_REQUEST_VERB.test(row)
                    && !NON_WORK_REQUEST.test(row)
                )),
        );
        if (sectionRows.length > 0) return sectionRows;
    }
    const rows = normaliseRequestedScopeRows(source
        .split('\n')
        .map(line => line.match(EXPLICIT_SCOPE_ROW)?.[1])
        .filter((value): value is string => typeof value === 'string'));
    return rows.length >= 2 ? rows : [];
}

export function createVerifiedRequestedScope(
    rfqContent: unknown,
    provenance: VerifiedRfqContentProvenance,
    requestedRows: readonly unknown[] = extractExplicitRequestedScopeRows(rfqContent),
): VerifiedRequestedScope | null {
    if (!isVerifiedRfqContentProvenance(provenance, rfqContent)) return null;
    const sourceText = comparableSourceText(rfqContent);
    const rows = normaliseRequestedScopeRows(requestedRows);
    if (rows.length === 0 || rows.some(row => !sourceText.includes(comparableSourceText(row)))) {
        return null;
    }
    return {
        version: 1,
        source: 'verified-rfq-document',
        sourceId: provenance.sourceId,
        sourceName: provenance.sourceName,
        classification: 'rfq',
        rfqContentFingerprint: provenance.contentFingerprint,
        sourceContentFingerprint: provenance.sourceContentFingerprint,
        provenanceFingerprint: provenance.provenanceFingerprint,
        contentFingerprint: fingerprintSourceContent(JSON.stringify(rows)),
        rows,
    };
}

export function resolveVerifiedRfqScope(formData: LooseRecord | null | undefined): ResolvedVerifiedRfqScope | null {
    const rfqContent = formData?.rfqContent;
    const rawProvenance = formData?.rfqContentProvenance;
    if (!isVerifiedRfqContentProvenance(rawProvenance, rfqContent)) return null;
    const provenance = rawProvenance;
    const rawRequestedScope = formData?.verifiedRequestedScope;
    const requestedScope = rawRequestedScope && typeof rawRequestedScope === 'object'
        ? rawRequestedScope as unknown as VerifiedRequestedScope
        : createVerifiedRequestedScope(rfqContent, provenance);
    if (!requestedScope) return null;
    const rows = Array.isArray(requestedScope.rows)
        ? requestedScope.rows.map(row => normaliseProvenanceText(row)).filter(Boolean)
        : [];
    const sourceText = comparableSourceText(rfqContent);
    const valid = requestedScope.version === 1
        && requestedScope.source === 'verified-rfq-document'
        && requestedScope.classification === 'rfq'
        && requestedScope.sourceId === provenance.sourceId
        && requestedScope.sourceName === provenance.sourceName
        && requestedScope.rfqContentFingerprint === provenance.contentFingerprint
        && requestedScope.sourceContentFingerprint === provenance.sourceContentFingerprint
        && requestedScope.provenanceFingerprint === provenance.provenanceFingerprint
        && requestedScope.contentFingerprint === fingerprintSourceContent(JSON.stringify(rows))
        && rows.length > 0
        && rows.every(row => sourceText.includes(comparableSourceText(row)));
    return valid ? { provenance, requestedScope, rows } : null;
}
